<template>
  <div class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5)">
    <div class="modal-dialog modal-lg modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Record Service - Visit #{{ visitId }}</h5>
          <button type="button" class="btn-close" @click="$emit('close')"></button>
        </div>
        <div class="modal-body">
          <!-- Service Categories -->
          <div class="mb-4">
            <h6 class="mb-3">Select Service Category</h6>
            <div class="row g-2">
              <div v-for="category in serviceCategories" :key="category.id" class="col-md-4 col-sm-6">
                <div class="card service-category-card"
                     :class="{'border-primary bg-light': selectedCategory === category.id}"
                     @click="selectCategory(category.id)">
                  <div class="card-body text-center p-3">
                    <i :class="category.icon + ' mb-2'" style="font-size: 32px;"
                       :style="{color: selectedCategory === category.id ? '#556ee6' : '#74788d'}"></i>
                    <h6 class="mb-0">{{ category.name }}</h6>
                    <small class="text-muted">{{ category.count }} services</small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Service Types -->
          <div v-if="selectedCategory" class="mb-4">
            <h6 class="mb-3">Service Type</h6>
            <div class="list-group">
              <label v-for="service in filteredServices" :key="service.type"
                     class="list-group-item list-group-item-action d-flex align-items-center">
                <input class="form-check-input me-3" type="radio"
                       :name="'service_' + service.type"
                       :value="service.type"
                       v-model="selectedService">
                <div class="flex-grow-1">
                  <h6 class="mb-0">{{ service.name }}</h6>
                  <small class="text-muted">{{ service.description }}</small>
                </div>
              </label>
            </div>
          </div>

          <!-- Member Selection (if service requires) -->
          <div v-if="selectedService && requiresMember" class="mb-4">
            <h6 class="mb-3">Select Member</h6>

            <!-- Quick member selection -->
            <div class="row g-2 mb-3">
              <div v-for="member in householdMembers" :key="member.member_id" class="col-md-6">
                <div class="form-check card p-3"
                     :class="{'border-primary': selectedMember?.member_id === member.member_id}">
                  <input class="form-check-input" type="radio"
                         :id="'member_' + member.member_id"
                         :value="member.member_id"
                         v-model="selectedMemberId"
                         @change="selectMember(member)">
                  <label class="form-check-label ms-2 w-100"
                         :for="'member_' + member.member_id">
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        <strong>{{ member.first_name }} {{ member.surname }}</strong>
                        <br>
                        <small class="text-muted">
                          {{ member.gender }} • {{ getAge(member.date_of_birth) }}
                          <span v-if="member.relationship_to_head" class="ms-1">
                            • {{ member.relationship_to_head }}
                          </span>
                        </small>
                      </div>
                      <div>
                        <span v-if="member.cbhi_number" class="badge bg-success">CBHI</span>
                        <span v-if="member.is_pregnant" class="badge bg-warning ms-1">Pregnant</span>
                      </div>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            <!-- Add household option -->
            <div class="form-check">
              <input class="form-check-input" type="radio" id="member_household"
                     value="household" v-model="selectedMemberId"
                     @change="selectMember(null)">
              <label class="form-check-label" for="member_household">
                Entire Household
              </label>
            </div>
          </div>

          <!-- Service Details -->
          <div v-if="selectedService" class="mb-4">
            <h6 class="mb-3">Service Details</h6>

            <!-- Dynamic fields based on service type -->
            <div v-if="selectedService === 'health_education'">
              <div class="mb-3">
                <label class="form-label">Topic Covered</label>
                <select v-model="serviceDetails.topic" class="form-select" required>
                  <option value="">Select topic</option>
                  <option value="hygiene">Personal Hygiene</option>
                  <option value="nutrition">Nutrition</option>
                  <option value="maternal_health">Maternal Health</option>
                  <option value="child_health">Child Health</option>
                  <option value="disease_prevention">Disease Prevention</option>
                  <option value="family_planning">Family Planning</option>
                  <option value="environmental_health">Environmental Health</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div class="mb-3">
                <label class="form-label">Number of Participants</label>
                <input v-model.number="serviceDetails.participants" type="number"
                       class="form-control" min="1">
              </div>
            </div>

            <div v-else-if="selectedService === 'vital_signs'">
              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label">Blood Pressure</label>
                  <div class="input-group">
                    <input v-model="serviceDetails.bp_systolic" type="number"
                           class="form-control" placeholder="Systolic">
                    <span class="input-group-text">/</span>
                    <input v-model="serviceDetails.bp_diastolic" type="number"
                           class="form-control" placeholder="Diastolic">
                  </div>
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label">Temperature (°C)</label>
                  <input v-model="serviceDetails.temperature" type="number"
                         class="form-control" step="0.1" min="35" max="42">
                </div>
              </div>
              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label">Pulse Rate</label>
                  <input v-model="serviceDetails.pulse" type="number"
                         class="form-control" min="40" max="200">
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label">Weight (kg)</label>
                  <input v-model="serviceDetails.weight" type="number"
                         class="form-control" step="0.1">
                </div>
              </div>
            </div>

            <div v-else-if="selectedService === 'medication_dispensed'">
              <div class="mb-3">
                <label class="form-label">Medication Name</label>
                <input v-model="serviceDetails.medication" type="text"
                       class="form-control" required>
              </div>
              <div class="row">
                <div class="col-md-6 mb-3">
                  <label class="form-label">Quantity</label>
                  <input v-model.number="serviceDetails.quantity" type="number"
                         class="form-control" min="1">
                </div>
                <div class="col-md-6 mb-3">
                  <label class="form-label">Unit</label>
                  <select v-model="serviceDetails.unit" class="form-select">
                    <option value="tablets">Tablets</option>
                    <option value="capsules">Capsules</option>
                    <option value="bottles">Bottles</option>
                    <option value="sachets">Sachets</option>
                    <option value="vials">Vials</option>
                  </select>
                </div>
              </div>
            </div>

            <div v-else-if="selectedService === 'wound_care'">
              <div class="mb-3">
                <label class="form-label">Type of Wound</label>
                <select v-model="serviceDetails.wound_type" class="form-select">
                  <option value="cut">Cut/Laceration</option>
                  <option value="burn">Burn</option>
                  <option value="abrasion">Abrasion</option>
                  <option value="puncture">Puncture</option>
                  <option value="surgical">Surgical</option>
                  <option value="ulcer">Ulcer</option>
                </select>
              </div>
              <div class="mb-3">
                <label class="form-label">Treatment Provided</label>
                <textarea v-model="serviceDetails.treatment" class="form-control" rows="2"></textarea>
              </div>
            </div>

            <div v-else-if="selectedService === 'family_planning'">
              <div class="mb-3">
                <label class="form-label">Method Provided</label>
                <select v-model="serviceDetails.fp_method" class="form-select">
                  <option value="pills">Oral Contraceptive Pills</option>
                  <option value="injection">Injectable</option>
                  <option value="implant">Implant</option>
                  <option value="iud">IUD</option>
                  <option value="condoms">Condoms</option>
                  <option value="counseling">Counseling Only</option>
                </select>
              </div>
              <div v-if="serviceDetails.fp_method && serviceDetails.fp_method !== 'counseling'" class="mb-3">
                <label class="form-label">Duration/Quantity</label>
                <input v-model="serviceDetails.duration" type="text" class="form-control">
              </div>
            </div>

            <!-- General notes for all services -->
            <div class="mb-3">
              <label class="form-label">Additional Notes</label>
              <textarea v-model="serviceDetails.notes" class="form-control" rows="2"
                        placeholder="Any additional information about the service..."></textarea>
            </div>

            <!-- Referral option -->
            <div class="form-check">
              <input v-model="serviceDetails.referralNeeded" class="form-check-input"
                     type="checkbox" id="referralCheck">
              <label class="form-check-label" for="referralCheck">
                Referral needed for this case
              </label>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="$emit('close')">Cancel</button>

          <button type="button" class="btn btn-success" @click="recordService"
                  :disabled="!canSubmit || submitting">
            <span v-if="submitting">
              <span class="spinner-border spinner-border-sm me-1"></span> Recording...
            </span>
            <span v-else>
              <i class="bx bx-check"></i> Record Service
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, executeJson } from "@/api";

export default {
  name: "ServiceRecordingModal",
  props: {
    visitId: {
      type: Number,
      required: true
    },
    householdId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      submitting: false,

      // Service selection
      selectedCategory: null,
      selectedService: null,
      selectedMember: null,
      selectedMemberId: null,

      // Household members
      householdMembers: [],

      // Service categories
      serviceCategories: [
        {
          id: 'clinical',
          name: 'Clinical Services',
          icon: 'bx bx-pulse',
          count: 5
        },
        {
          id: 'maternal',
          name: 'Maternal & Child',
          icon: 'bx bx-female',
          count: 4
        },
        {
          id: 'preventive',
          name: 'Preventive Care',
          icon: 'bx bx-shield-plus',
          count: 6
        },
        {
          id: 'education',
          name: 'Health Education',
          icon: 'bx bx-book-open',
          count: 3
        },
        {
          id: 'environmental',
          name: 'Environmental',
          icon: 'bx bx-home-smile',
          count: 4
        },
        {
          id: 'other',
          name: 'Other Services',
          icon: 'bx bx-dots-horizontal-rounded',
          count: 3
        }
      ],

      // Available services
      services: [
        // Clinical Services
        {
          category: 'clinical',
          type: 'vital_signs',
          name: 'Vital Signs Check',
          description: 'Blood pressure, temperature, pulse, weight',
          requiresMember: true
        },
        {
          category: 'clinical',
          type: 'wound_care',
          name: 'Wound Care',
          description: 'Dressing, cleaning, minor treatment',
          requiresMember: true
        },
        {
          category: 'clinical',
          type: 'medication_dispensed',
          name: 'Medication Dispensed',
          description: 'ORS, paracetamol, vitamins, etc.',
          requiresMember: true
        },
        {
          category: 'clinical',
          type: 'first_aid',
          name: 'First Aid',
          description: 'Emergency or minor injury treatment',
          requiresMember: true
        },
        {
          category: 'clinical',
          type: 'screening',
          name: 'Health Screening',
          description: 'TB, HIV, malaria screening',
          requiresMember: true
        },

        // Maternal & Child
        {
          category: 'maternal',
          type: 'anc_followup',
          name: 'ANC Follow-up',
          description: 'Antenatal care check',
          requiresMember: true
        },
        {
          category: 'maternal',
          type: 'pnc_visit',
          name: 'Postnatal Care',
          description: 'Mother and newborn check',
          requiresMember: true
        },
        {
          category: 'maternal',
          type: 'growth_monitoring',
          name: 'Growth Monitoring',
          description: 'Weight, height, MUAC measurement',
          requiresMember: true
        },
        {
          category: 'maternal',
          type: 'family_planning',
          name: 'Family Planning',
          description: 'Counseling and method provision',
          requiresMember: true
        },

        // Preventive Care
        {
          category: 'preventive',
          type: 'immunization',
          name: 'Immunization',
          description: 'Vaccine administration',
          requiresMember: true
        },
        {
          category: 'preventive',
          type: 'vitamin_a',
          name: 'Vitamin A Supplementation',
          description: 'Vitamin A dose for children',
          requiresMember: true
        },
        {
          category: 'preventive',
          type: 'deworming',
          name: 'Deworming',
          description: 'Deworming medication',
          requiresMember: true
        },
        {
          category: 'preventive',
          type: 'nutritional_counseling',
          name: 'Nutritional Counseling',
          description: 'Diet and feeding advice',
          requiresMember: true
        },
        {
          category: 'preventive',
          type: 'disease_prevention',
          name: 'Disease Prevention',
          description: 'Mosquito net distribution, etc.',
          requiresMember: false
        },

        // Health Education
        {
          category: 'education',
          type: 'health_education',
          name: 'Health Education Session',
          description: 'Group or individual education',
          requiresMember: false
        },
        {
          category: 'education',
          type: 'demonstration',
          name: 'Health Demonstration',
          description: 'Handwashing, food prep, etc.',
          requiresMember: false
        },
        {
          category: 'education',
          type: 'counseling',
          name: 'Health Counseling',
          description: 'Individual counseling session',
          requiresMember: true
        },

        // Environmental
        {
          category: 'environmental',
          type: 'water_treatment',
          name: 'Water Treatment',
          description: 'Water purification supplies',
          requiresMember: false
        },
        {
          category: 'environmental',
          type: 'latrine_promotion',
          name: 'Latrine Promotion',
          description: 'Sanitation counseling',
          requiresMember: false
        },
        {
          category: 'environmental',
          type: 'waste_management',
          name: 'Waste Management',
          description: 'Waste disposal education',
          requiresMember: false
        },
        {
          category: 'environmental',
          type: 'vector_control',
          name: 'Vector Control',
          description: 'Mosquito breeding site removal',
          requiresMember: false
        },

        // Other Services
        {
          category: 'other',
          type: 'referral',
          name: 'Referral Service',
          description: 'Referral to health facility',
          requiresMember: true
        },
        {
          category: 'other',
          type: 'follow_up',
          name: 'Follow-up Visit',
          description: 'Check on previous condition',
          requiresMember: true
        },
        {
          category: 'other',
          type: 'other',
          name: 'Other Service',
          description: 'Any other service provided',
          requiresMember: false
        }
      ],

      // Service details form
      serviceDetails: {
        notes: '',
        referralNeeded: false
      }
    };
  },

  computed: {
    filteredServices() {
      if (!this.selectedCategory) return [];
      return this.services.filter(s => s.category === this.selectedCategory);
    },

    requiresMember() {
      if (!this.selectedService) return false;
      const service = this.services.find(s => s.type === this.selectedService);
      return service ? service.requiresMember : false;
    },

    canSubmit() {
      if (!this.selectedService) return false;
      if (this.requiresMember && !this.selectedMemberId) return false;

      // Additional validation based on service type
      if (this.selectedService === 'health_education' && !this.serviceDetails.topic) return false;
      if (this.selectedService === 'medication_dispensed' && !this.serviceDetails.medication) return false;

      return true;
    }
  },

  mounted() {
    this.loadHouseholdMembers();
  },

  methods: {
    async loadHouseholdMembers() {
      this.loading = true;

      const data = {
        "function": "getHouseholdDetails",
        "householdId": this.householdId
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.householdMembers = res.data.data.members || [];
        }
      } catch (e) {
        console.error("Failed to load household members:", e);
      } finally {
        this.loading = false;
      }
    },

    selectCategory(categoryId) {
      this.selectedCategory = categoryId;
      this.selectedService = null;
      this.selectedMember = null;
      this.selectedMemberId = null;
    },

    selectMember(member) {
      this.selectedMember = member;
      if (!member) {
        this.selectedMemberId = 'household';
      }
    },

    async recordService() {
      if (!this.canSubmit) return;

      this.submitting = true;

      const service = this.services.find(s => s.type === this.selectedService);

      const data = {
        "function": "recordService",
        "visitId": this.visitId,
        "memberId": this.selectedMemberId === 'household' ? null : this.selectedMemberId,
        "serviceType": this.selectedService,
        "serviceCategory": this.selectedCategory,
        "serviceDetails": JSON.stringify({
          ...this.serviceDetails,
          serviceName: service.name
        }),
        "quantity": this.serviceDetails.quantity || 1
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          this.$emit('recorded', {
            serviceId: res.data.data.serviceId,
            referralNeeded: this.serviceDetails.referralNeeded
          });
        } else {
          alert(res.data.message || 'Failed to record service');
        }
      } catch (e) {
        console.error("Failed to record service:", e);
        alert('Failed to record service. Please try again.');
      } finally {
        this.submitting = false;
      }
    },

    getAge(dateOfBirth) {
      if (!dateOfBirth) return 'Unknown';

      const today = new Date();
      const birthDate = new Date(dateOfBirth);
      const ageInYears = today.getFullYear() - birthDate.getFullYear();

      if (ageInYears < 1) {
        const ageInMonths = (today.getFullYear() - birthDate.getFullYear()) * 12 +
            (today.getMonth() - birthDate.getMonth());
        return `${ageInMonths} months`;
      }

      return `${ageInYears} years`;
    }
  }
};
</script>

<style scoped>
.modal {
  display: block;
}

.service-category-card {
  cursor: pointer;
  transition: all 0.2s;
}

.service-category-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.service-category-card.border-primary {
  border-width: 2px !important;
}

.list-group-item {
  cursor: pointer;
}

.list-group-item:hover {
  background-color: #f8f9fa;
}

.form-check.card {
  cursor: pointer;
  transition: all 0.2s;
}

.form-check.card:hover {
  border-color: #556ee6;
}

.form-check.card.border-primary {
  background-color: rgba(85, 110, 230, 0.05);
  border-width: 2px !important;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.15em;
}
</style>